import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Video, ShieldCheck, Lock, Users, Clock } from "lucide-react";
import { Reveal, MaskedLine, Marquee } from "@/components/Reveal";
import { usePageTitle } from "@/lib/usePageTitle";
import { ISSUES, STEPS, SITE } from "@/lib/content";

const HERO_IMG = "https://images.pexels.com/photos/27548393/pexels-photo-27548393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ROOM_IMG = "https://images.pexels.com/photos/26932519/pexels-photo-26932519.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SESSION_IMG = "https://images.pexels.com/photos/7195081/pexels-photo-7195081.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PORTRAIT_IMG = "/images/brian-home.jpg";

const TRUST = [
  { icon: Lock, label: "Confidential & GDPR-compliant" },
  { icon: ShieldCheck, label: "IACP member · BSc (IICP)" },
  { icon: Users, label: "Adults 18+" },
  { icon: Clock, label: "In-person & online" },
];

const Home = () => {
  usePageTitle(
    "Home",
    "Warm, confidential psychotherapy and counselling for adults in Marino, Dublin 3 and online worldwide."
  );
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative overflow-hidden">
        <motion.div
          style={{ y: glowY }}
          className="pointer-events-none absolute -top-24 right-[8%] h-96 w-96 rounded-full bg-[#E6DEC3]/50 blur-3xl"
          aria-hidden="true"
        />
        <div className="mx-auto grid max-w-6xl items-center gap-14 px-5 pb-20 pt-16 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:pt-24">
          <div>
            <Reveal y={12}>
              <p className="eyebrow" data-testid="hero-eyebrow">Psychotherapy & Counselling · Marino, Dublin 3 & Online</p>
            </Reveal>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.12] tracking-tight text-[#1E2723] sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>When life feels heavy,</MaskedLine>
              <MaskedLine delay={0.3} className="italic text-[#527363]">you don’t have to carry</MaskedLine>
              <MaskedLine delay={0.45} className="italic text-[#527363]">it alone.</MaskedLine>
            </h1>
            <Reveal delay={0.65}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-[#4A5750] sm:text-lg" data-testid="hero-subtitle">
                Warm, confidential psychotherapy and counselling for adults — in person at the practice in {SITE.location}, or online, wherever you are in the world.
              </p>
            </Reveal>
            <Reveal delay={0.8}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link to="/booking" data-testid="hero-book-cta" className="btn-primary">
                  Book an appointment <ArrowRight size={16} />
                </Link>
                <Link to="/therapy" data-testid="hero-explore-cta" className="btn-secondary">
                  Explore therapy options
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.95}>
              <ul className="mt-12 flex flex-wrap gap-x-7 gap-y-3" data-testid="trust-strip">
                {TRUST.map((t) => (
                  <li key={t.label} className="flex items-center gap-2 text-sm font-medium text-[#54645B]">
                    <t.icon size={16} className="text-[#527363]" /> {t.label}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <motion.div style={{ y: imgY }} className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="arch-frame overflow-hidden border border-[#E2DACD] shadow-[0_24px_60px_-16px_rgba(30,39,35,0.25)]">
              <img src={HERO_IMG} alt="Warm sunlight streaming through tranquil forest trees" className="h-[420px] w-full object-cover sm:h-[520px]" />
            </div>
            <div className="float-slow soft-card absolute -bottom-6 -left-4 max-w-[240px] p-5 sm:-left-10" data-testid="hero-quote-card">
              <p className="font-serif text-lg italic leading-snug text-[#2A3630]">“A space to slow down, and to be heard.”</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee items={["A space to be heard", "In person in Marino", "Online worldwide", "Confidential", "At your own pace"]} />

      <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8" aria-labelledby="pathways-heading">
        <Reveal>
          <p className="eyebrow">Two ways to attend</p>
          <h2 id="pathways-heading" className="mt-4 max-w-2xl font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl lg:text-4xl">
            Choose the setting that feels right for you
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <Reveal delay={0.1}>
            <Link to="/booking" data-testid="pathway-in-person-card" className="group soft-card block overflow-hidden transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5">
              <div className="h-52 overflow-hidden">
                <img src={ROOM_IMG} alt="Warm, peaceful counselling armchair in the Marino practice room" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-7">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#E3ECE7] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#527363]">
                  <MapPin size={13} /> In person
                </span>
                <h3 className="mt-4 font-serif text-2xl font-medium text-[#1E2723]">At the practice in Marino</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#54645B]">
                  A calm, private room in Marino, Dublin 3 — a dedicated space away from everyday demands, with time that is entirely yours.
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#B85C43]">
                  Book an in-person session <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/booking" data-testid="pathway-online-card" className="group soft-card block overflow-hidden transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5">
              <div className="h-52 overflow-hidden">
                <img src={SESSION_IMG} alt="A client speaking with their therapist by video call from the comfort of home" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-7">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#F8EBE7] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#B85C43]">
                  <Video size={13} /> Online
                </span>
                <h3 className="mt-4 font-serif text-2xl font-medium text-[#1E2723]">Online, anywhere in the world</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#54645B]">
                  Secure video sessions from your own private space — at home, studying away, or living abroad as part of the Irish diaspora.
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#B85C43]">
                  Book an online session <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F2ECE1] py-24" aria-labelledby="issues-heading">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">What therapy can help with</p>
            <h2 id="issues-heading" className="mt-4 max-w-2xl font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl lg:text-4xl">
              Whatever you are holding, you will be met with compassion and presence
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4A5750]">
              Adults come to therapy for many reasons. You do not need the right words yet — only a sense that something needs your attention.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {ISSUES.map((issue, i) => (
              <Reveal key={issue.title} delay={0.06 * i}>
                <div className="soft-card h-full p-6 transition-[transform] duration-300 hover:-translate-y-1" data-testid={`issue-card-${i}`}>
                  <span className="block h-1 w-8 rounded-full bg-[#B85C43]" />
                  <h3 className="mt-4 text-lg font-semibold text-[#1E2723]">{issue.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#54645B]">{issue.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8" aria-labelledby="steps-heading">
        <div className="grid items-start gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <p className="eyebrow">What to expect</p>
            <h2 id="steps-heading" className="mt-4 font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl lg:text-4xl">
              Beginning is simpler than it feels
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-[#4A5750]">
              Reaching out is often the hardest step. Everything after that happens at your pace, with no pressure and no judgement.
            </p>
            <Link to="/booking" data-testid="steps-book-cta" className="btn-primary mt-8">
              Take the first step <ArrowRight size={16} />
            </Link>
          </Reveal>
          <ol className="space-y-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={0.12 * i}>
                <li className="soft-card flex gap-6 p-7" data-testid={`step-card-${i}`}>
                  <span className="font-serif text-4xl font-medium text-[#B85C43]/70">{s.n}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-[#1E2723]">{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-[#54645B]">{s.text}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-24 sm:px-8">
        <Reveal>
          <div className="soft-card grid items-center gap-10 overflow-hidden p-8 sm:p-12 lg:grid-cols-[0.75fr_1.25fr]">
            <div className="arch-frame mx-auto w-full max-w-xs overflow-hidden">
              <img src={PORTRAIT_IMG} alt="Brian Boland, Psychotherapist and Counsellor" className="h-80 w-full object-cover" />
            </div>
            <div>
              <p className="eyebrow">Meet your therapist</p>
              <h2 className="mt-4 font-serif text-2xl font-medium leading-snug text-[#1E2723] sm:text-3xl">
                A warm, steady presence
              </h2>
              <p className="mt-5 text-base leading-relaxed text-[#4A5750]">
                Brian offers a safe, non-judgmental space where you can speak openly and be genuinely heard. His approach is human and unhurried, shaped around you as an individual rather than a set of symptoms.
              </p>
              <Link to="/about" data-testid="home-about-cta" className="btn-secondary mt-7">
                More about Brian <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#1E2723] py-20 text-center" aria-labelledby="cta-heading">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <h2 id="cta-heading" className="font-serif text-2xl font-medium leading-snug text-[#FAF7F2] sm:text-3xl lg:text-4xl">
              Ready when you are
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#B9C4BD]">
              Send a confidential enquiry and Brian will be in touch to arrange a first session — in person in Marino or online.
            </p>
            <Link to="/booking" data-testid="bottom-book-cta" className="btn-primary mt-8">
              Book an appointment <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
